/**
 * Long-range trends: each baby's bottles, changes, and sleep, averaged week by
 * week or month by month of life. The overview's Weeks and Months charts count
 * on the phone's calendar; an assistant can count on the household's.
 *
 * The rules are the pediatrician report's, so a day nobody logged never reads
 * as a day the baby didn't eat, change, or sleep: a kind is averaged over the
 * days it was logged, leaving out today and the day logging began, and a
 * night counts only once it is over. Sleeps over `MAX_PLAUSIBLE_SLEEP_MS` are
 * missed Wake up taps and left out.
 *
 * Two steps: `countedDays` puts each entry on its calendar date (one pass over
 * the arrays, whatever the zoom), and `periodRows` groups the dates into weeks
 * or months of life. Dates are calendar strings, so a week across a clock
 * change is still seven days.
 */
import { ageInDays, ageInMonths } from "./age.ts";
import type { Calendar } from "./calendar.ts";
import { MAX_PLAUSIBLE_SLEEP_MS } from "./forgotten.ts";
import { roundOz } from "./units.ts";

export type PeriodUnit = "week" | "month";

/** The most periods a chart shows: about six months of weeks, a year of months. */
export const PERIODS_SHOWN: Record<PeriodUnit, number> = { week: 26, month: 12 };

export type ChangeKind = "wet" | "dirty" | "both";

/**
 * One baby's bottles, changes, and sleeps as the server sends them: parallel
 * arrays, oldest first, times in ms since the epoch.
 */
export interface BabySeries {
  babyId: string;
  /** `oz` as stored; null for a bottle logged without ounces. */
  bottles: { at: readonly number[]; oz: readonly (number | null)[] };
  /** `kind` is null for a change logged without one. */
  changes: { at: readonly number[]; kind: readonly (ChangeKind | null)[] };
  /** `end` is null for a sleep still going. */
  sleeps: { at: readonly number[]; end: readonly (number | null)[] };
}

export interface BottleDay {
  /** Ounces as stored. A bottle without ounces adds none but counts. */
  oz: number;
  bottles: number;
}

export interface ChangeDay {
  changes: number;
  /** Wet changes, a change that is both included. */
  wet: number;
  /** Dirty changes, a change that is both included. */
  dirty: number;
  /** Changes both wet and dirty. */
  both: number;
}

/** A night, noon to noon on the wall clock. */
export interface Night {
  /** Time asleep inside the night. */
  totalMs: number;
  /** The longest finished sleep that began in the night; null with none. */
  longestMs: number | null;
}

/** A baby's counted days and nights, by calendar date (YYYY-MM-DD). */
export interface CountedDays {
  bottles: Map<string, BottleDay>;
  changes: Map<string, ChangeDay>;
  /** By the date of the noon each night starts at. */
  nights: Map<string, Night>;
}

const NOON = 12 * 60;

/**
 * The noon that starts the night `t` is in: today's, or yesterday's before
 * noon. With `now`, it is the start of the night still going, so every night
 * that ends by it is over.
 */
export function currentNightStart(t: Date, calendar: Calendar): Date {
  return calendar.minuteOfDay(t) >= NOON
    ? calendar.atMinute(t, NOON)
    : calendar.atMinute(calendar.addDays(t, -1), NOON);
}

const overlap = (a0: number, a1: number, b0: number, b1: number) =>
  Math.max(0, Math.min(a1, b1) - Math.max(a0, b0));

/**
 * One baby's counted days: each date with a bottle (or a change) logged,
 * before `today` and not the day logging began (`began`, the household's first
 * entry), and each night with sleep logged that ended by `nightCutoff` (see
 * `currentNightStart`) and began after `began`. A sleep still going counts up
 * to the cutoff, so the counts hold still until the next noon.
 */
export function countedDays(
  series: BabySeries,
  began: Date | null,
  today: string,
  nightCutoff: Date,
  calendar: Calendar,
): CountedDays {
  const beganDay = began ? calendar.isoDate(began) : null;
  const counts = (day: string) => day < today && day !== beganDay;

  const bottles = new Map<string, BottleDay>();
  const b = series.bottles;
  for (let i = 0; i < b.at.length; i++) {
    const day = calendar.isoDate(new Date(b.at[i]!));
    if (!counts(day)) continue;
    let d = bottles.get(day);
    if (!d) bottles.set(day, (d = { oz: 0, bottles: 0 }));
    d.oz += b.oz[i] ?? 0;
    d.bottles += 1;
  }
  for (const d of bottles.values()) d.oz = roundOz(d.oz);

  const changes = new Map<string, ChangeDay>();
  const c = series.changes;
  for (let i = 0; i < c.at.length; i++) {
    const day = calendar.isoDate(new Date(c.at[i]!));
    if (!counts(day)) continue;
    let d = changes.get(day);
    if (!d) changes.set(day, (d = { changes: 0, wet: 0, dirty: 0, both: 0 }));
    const kind = c.kind[i];
    d.changes += 1;
    if (kind === "wet" || kind === "both") d.wet += 1;
    if (kind === "dirty" || kind === "both") d.dirty += 1;
    if (kind === "both") d.both += 1;
  }

  return { bottles, changes, nights: countedNights(series, began, nightCutoff, calendar) };
}

interface NightTally extends Night {
  start: number;
  end: number;
}

function countedNights(
  series: BabySeries,
  began: Date | null,
  nightCutoff: Date,
  calendar: Calendar,
): Map<string, Night> {
  const cutoff = nightCutoff.getTime();
  const tallies = new Map<string, NightTally>();
  /** The night that starts at the noon `start` (an instant at local noon). */
  const nightAt = (start: Date): [string, NightTally] => {
    const key = calendar.isoDate(start);
    let night = tallies.get(key);
    if (!night) {
      const end = calendar.atMinute(calendar.addDays(start, 1), NOON).getTime();
      tallies.set(key, (night = { start: start.getTime(), end, totalMs: 0, longestMs: null }));
    }
    return [key, night];
  };

  const s = series.sleeps;
  for (let i = 0; i < s.at.length; i++) {
    const at = s.at[i]!;
    const ended = s.end[i] ?? null;
    const end = ended ?? cutoff;
    // A sleep too long to be real is a missed Wake up tap, as in the report.
    if (end <= at || end - at > MAX_PLAUSIBLE_SLEEP_MS) continue;
    const [, first] = nightAt(currentNightStart(new Date(at), calendar));
    first.totalMs += overlap(at, end, first.start, first.end);
    if (ended !== null) first.longestMs = Math.max(first.longestMs ?? 0, ended - at);
    // Fourteen hours never reach past the next night (the shortest is 23).
    if (end > first.end) {
      const [, next] = nightAt(new Date(first.end));
      next.totalMs += overlap(at, end, next.start, next.end);
    }
  }

  const nights = new Map<string, Night>();
  for (const [key, n] of tallies) {
    const over = n.end <= cutoff;
    const logged = began === null || n.start >= began.getTime();
    if (over && logged && n.totalMs > 0)
      nights.set(key, { totalMs: n.totalMs, longestMs: n.longestMs });
  }
  return nights;
}

/** The week or month of life `date` is in: 0 for the first. Both are YYYY-MM-DD. */
export function periodOf(unit: PeriodUnit, birthDate: string, date: string): number {
  return unit === "week"
    ? Math.floor(ageInDays(birthDate, date) / 7)
    : ageInMonths(birthDate, date);
}

/** The periods a chart can show on `today`, or null before the birth date. */
export function periodSpan(
  unit: PeriodUnit,
  birthDate: string,
  today: string,
): { first: number; current: number } | null {
  if (birthDate > today) return null;
  const current = periodOf(unit, birthDate, today);
  return { first: Math.max(0, current - PERIODS_SHOWN[unit] + 1), current };
}

export interface Period {
  /** Completed weeks or months of life: 0 for the first. */
  n: number;
  /** The period today is in: not over, so hatched. */
  current: boolean;
  /** Counted days (nights, for sleep). With none, the period draws no bar. */
  days: number;
}

export interface BottlePeriod extends Period {
  /** Ounces (as stored) and bottles on the counted days. */
  oz: number;
  bottles: number;
  /** Ounces a day over the counted days; null with none. */
  avgOz: number | null;
}

/** Changes on the counted days, by kind (see `ChangeDay`). */
export interface ChangePeriod extends Period, ChangeDay {}

export interface SleepPeriod extends Period {
  /** Time asleep a night over the counted nights; null with none. */
  avgMs: number | null;
  /** The longest stretch a night, over the counted nights that have one; null with none. */
  avgLongestMs: number | null;
}

export interface BabyPeriods {
  bottles: BottlePeriod[];
  changes: ChangePeriod[];
  sleep: SleepPeriod[];
}

type Span = { first: number; current: number };

/** Each shown period's counted days, by period. */
function byPeriod<D>(
  days: ReadonlyMap<string, D>,
  unit: PeriodUnit,
  birthDate: string,
  span: Span,
): Map<number, D[]> {
  const out = new Map<number, D[]>();
  for (const [date, d] of days) {
    if (date < birthDate) continue;
    const n = periodOf(unit, birthDate, date);
    if (n < span.first || n > span.current) continue;
    let list = out.get(n);
    if (!list) out.set(n, (list = []));
    list.push(d);
  }
  return out;
}

const sum = <D>(list: readonly D[], pick: (d: D) => number) =>
  list.reduce((n, d) => n + pick(d), 0);

function bottlePeriod(n: number, current: boolean, days: BottleDay[]): BottlePeriod {
  const oz = roundOz(sum(days, (d) => d.oz));
  return {
    n,
    current,
    days: days.length,
    oz,
    bottles: sum(days, (d) => d.bottles),
    avgOz: days.length ? roundOz(oz / days.length) : null,
  };
}

function changePeriod(n: number, current: boolean, days: ChangeDay[]): ChangePeriod {
  return {
    n,
    current,
    days: days.length,
    changes: sum(days, (d) => d.changes),
    wet: sum(days, (d) => d.wet),
    dirty: sum(days, (d) => d.dirty),
    both: sum(days, (d) => d.both),
  };
}

function sleepPeriod(n: number, current: boolean, nights: Night[]): SleepPeriod {
  const stretches = nights.flatMap((d) => (d.longestMs === null ? [] : [d.longestMs]));
  return {
    n,
    current,
    days: nights.length,
    avgMs: nights.length ? sum(nights, (d) => d.totalMs) / nights.length : null,
    avgLongestMs: stretches.length ? sum(stretches, (ms) => ms) / stretches.length : null,
  };
}

type Kind = keyof BabyPeriods;
const KINDS: readonly Kind[] = ["bottles", "changes", "sleep"];

/** One baby's counted days of each kind, by period. */
interface Grouped {
  span: Span;
  bottles: Map<number, BottleDay[]>;
  changes: Map<number, ChangeDay[]>;
  sleep: Map<number, Night[]>;
}

/** The periods from `start` through the current one, each made from its counted days. */
function rowsFrom<D, P extends Period>(
  groups: Map<number, D[]>,
  start: number | undefined,
  current: number,
  make: (n: number, current: boolean, days: D[]) => P,
): P[] {
  if (start === undefined || groups.size === 0) return [];
  const rows: P[] = [];
  for (let n = start; n <= current; n++) rows.push(make(n, n === current, groups.get(n) ?? []));
  return rows;
}

/**
 * Each baby's weeks or months of life for the long-range charts, oldest first,
 * the current period last (hatched: it isn't over). At most `PERIODS_SHOWN`,
 * from the first period with a counted day of that kind; babies with the same
 * birth date (twins) start together so their bars line up. A period with no
 * counted day is a gap (`days` 0).
 *
 * A baby with no birth date is left out of the map; one with no counted day
 * of a kind (or a birth date after `today`) gets no periods of that kind.
 */
export function periodRows(
  counted: ReadonlyMap<string, CountedDays>,
  babies: readonly { id: string; birthDate: string | null }[],
  today: string,
  unit: PeriodUnit,
): Map<string, BabyPeriods> {
  const grouped = new Map<string, Grouped>();
  // The first counted period of each kind for each birth date, so twins start together.
  const firstOf = new Map<string, Partial<Record<Kind, number>>>();

  for (const { id, birthDate } of babies) {
    if (birthDate === null) continue;
    const span = periodSpan(unit, birthDate, today);
    const days = counted.get(id);
    if (!span || !days) continue;
    const g: Grouped = {
      span,
      bottles: byPeriod(days.bottles, unit, birthDate, span),
      changes: byPeriod(days.changes, unit, birthDate, span),
      sleep: byPeriod(days.nights, unit, birthDate, span),
    };
    grouped.set(id, g);
    const first = firstOf.get(birthDate) ?? {};
    for (const kind of KINDS)
      for (const n of g[kind].keys()) first[kind] = Math.min(first[kind] ?? n, n);
    firstOf.set(birthDate, first);
  }

  const out = new Map<string, BabyPeriods>();
  for (const { id, birthDate } of babies) {
    if (birthDate === null) continue;
    const g = grouped.get(id);
    const first = firstOf.get(birthDate) ?? {};
    out.set(
      id,
      g
        ? {
            bottles: rowsFrom(g.bottles, first.bottles, g.span.current, bottlePeriod),
            changes: rowsFrom(g.changes, first.changes, g.span.current, changePeriod),
            sleep: rowsFrom(g.sleep, first.sleep, g.span.current, sleepPeriod),
          }
        : { bottles: [], changes: [], sleep: [] },
    );
  }
  return out;
}
